import { useMemo, useState, useContext, useRef } from "react";
import DOMPurify from "dompurify";
import { getToken } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { AppStateContext } from "../../state/AppProvider";

import { ChatResponse, getCitationFilePath, updateFeedback } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { ButtonTooltip } from "../ButtonTooltip";
import parse, { HTMLReactParserOptions, Element } from "html-react-parser";
import CodeCopyButton from "./CodeCopyButton";

interface Props {
    answer: ChatResponse;
    isStreaming: boolean;
    onCitationClicked: (filePath: string, citationTitle: string) => void;
    onThoughtProcessClicked: () => void;
    onCopyAnswerClicked: () => void;
    showThoughts?: boolean;
    lastAnswer: boolean;
    commentInput: string;
    setCommentInput: (commentInput: string) => void;
    windowWidth: number;
}

export const Answer = ({
    answer,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onCopyAnswerClicked,
    showThoughts,
    lastAnswer,
    commentInput,
    setCommentInput,
    windowWidth
}: Props) => {
    const client = useMsal().instance;
    const appStateContext = useContext(AppStateContext);
    // Helpful feedback refs
    const radioRef0 = useRef<HTMLInputElement>(null);
    const radioRef5 = useRef<HTMLInputElement>(null);
    const radioRef5_30 = useRef<HTMLInputElement>(null);
    const radioRef30 = useRef<HTMLInputElement>(null);

    // Unhelpful feedback refs
    const radioRefIrrelevant = useRef<HTMLInputElement>(null);
    const radioRefErrors = useRef<HTMLInputElement>(null);

    const thoughts = answer.choices[0].context.metadata?.thoughts;
    const messageContent = answer.choices[0].message.content;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(messageContent, isStreaming, onCitationClicked), [answer]);
    const messageId = answer.choices[0].message_id;
    const feedback = answer.choices[0].feedback;
    const feedbackComment = answer.choices[0].feedback_comment;

    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);

    const sanitizedThoughts = DOMPurify.sanitize(thoughts!);

    const [copyIcon, setCopyIcon] = useState<string>("content_copy");
    const [feedbackOptionSelected, setFeedbackOptionSelected] = useState<boolean>(false);

    let streamingDot: string =
        isStreaming && lastAnswer
            ? " <span class='material-symbols-outlined text-icon14 text-sstl-dark' style=\"font-variation-settings: 'FILL' 1;\">circle</span>"
            : "";

    function onThoughtsClicked() {
        onThoughtProcessClicked();
    }

    function onCopyClicked() {
        onCopyAnswerClicked();
        setCopyIcon("check");
        setTimeout(() => {
            setCopyIcon("content_copy");
        }, 2000);
    }

    const updateMessageFeedback = async (feedback: string | null) => {
        const token = client ? await getToken(client) : undefined;
        await updateFeedback(
            {
                message_id: messageId || "",
                feedback: feedback
            },
            token?.accessToken
        ).then(res => {
            if (res) {
                appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY_MESSAGE", payload: res });
            }
        });
    };

    const updateMessageFeedbackComment = async (feedback: string | null) => {
        const token = client ? await getToken(client) : undefined;
        await updateFeedback(
            {
                message_id: messageId || "",
                feedback: feedback,
                feedback_type: "comment"
            },
            token?.accessToken
        ).then(res => {
            if (res) {
                appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY_MESSAGE", payload: res });
            }
        });
    };

    const onFeedbackHelpful = () => {
        if (feedback === "Unhelpful") {
            // Clear all radio button selections
            radioRef0.current?.checked && (radioRef0.current.checked = false);
            radioRef5.current?.checked && (radioRef5.current.checked = false);
            radioRef5_30.current?.checked && (radioRef5_30.current.checked = false);
            radioRef30.current?.checked && (radioRef30.current.checked = false);
            radioRefIrrelevant.current?.checked && (radioRefIrrelevant.current.checked = false);
            radioRefErrors.current?.checked && (radioRefErrors.current.checked = false);
            setFeedbackOptionSelected(false);
            updateMessageFeedback("Helpful");
        } else if (feedback === "Helpful") {
            // Clear all radio button selections
            radioRef0.current?.checked && (radioRef0.current.checked = false);
            radioRef5.current?.checked && (radioRef5.current.checked = false);
            radioRef5_30.current?.checked && (radioRef5_30.current.checked = false);
            radioRef30.current?.checked && (radioRef30.current.checked = false);
            radioRefIrrelevant.current?.checked && (radioRefIrrelevant.current.checked = false);
            radioRefErrors.current?.checked && (radioRefErrors.current.checked = false);
            updateMessageFeedback(null);
            setFeedbackOptionSelected(false);
        } else {
            updateMessageFeedback("Helpful");
        }
    };

    const onFeedbackUnhelpful = () => {
        if (feedback === "Helpful") {
            // Clear all radio button selections
            radioRef0.current?.checked && (radioRef0.current.checked = false);
            radioRef5.current?.checked && (radioRef5.current.checked = false);
            radioRef5_30.current?.checked && (radioRef5_30.current.checked = false);
            radioRef30.current?.checked && (radioRef30.current.checked = false);
            radioRefIrrelevant.current?.checked && (radioRefIrrelevant.current.checked = false);
            radioRefErrors.current?.checked && (radioRefErrors.current.checked = false);
            setFeedbackOptionSelected(false);
            updateMessageFeedback("Unhelpful");
        } else if (feedback === "Unhelpful") {
            // Clear all radio button selections
            radioRef0.current?.checked && (radioRef0.current.checked = false);
            radioRef5.current?.checked && (radioRef5.current.checked = false);
            radioRef5_30.current?.checked && (radioRef5_30.current.checked = false);
            radioRef30.current?.checked && (radioRef30.current.checked = false);
            radioRefIrrelevant.current?.checked && (radioRefIrrelevant.current.checked = false);
            radioRefErrors.current?.checked && (radioRefErrors.current.checked = false);
            updateMessageFeedback(null);
            setFeedbackOptionSelected(false);
        } else {
            updateMessageFeedback("Unhelpful");
        }
    };

    const sendComment = async () => {
        if (!feedbackOptionSelected) {
            return;
        }
        await updateMessageFeedbackComment(commentInput);
        setFeedbackOptionSelected(false);
    };

    const handleRadioChange = (value: string) => {
        setFeedbackOptionSelected(true);
        setCommentInput(value);
    };

    const options: HTMLReactParserOptions = {
        replace: domNode => {
            if (domNode instanceof Element && domNode.name === "span" && domNode.attribs?.id === "code-btn") {
                return <CodeCopyButton />;
            }
        }
    };

    const content = parsedAnswer.answerHtml + streamingDot;

    return (
        <div className="w-full">
            <div className={"transition-[padding] duration-200 py-[18px] px-4 text-base m-auto "}>
                <div className="mx-auto flex flex-1 text-base gap-4 lg:gap-6 lg:max-w-3xl">
                    <div className="flex-shrink-0 flex flex-col relative items-end">
                        <div>
                            <div className="pt-0">
                                <AnswerIcon animate={isStreaming} />
                            </div>
                        </div>
                    </div>
                    <div className="relative flex w-full min-w-0 flex-col">
                        <div className="flex-col gap-1 lg:gap-3">
                            <div className="flex flex-grow flex-col max-w-full">
                                <div className="flex flex-col gap-1 lg:gap-3">
                                    <div className="flex flex-grow flex-col max-w-full">
                                        <div className="min-h-[20px] text-message flex flex-col whitespace-pre-wrap break-words w-full items-end overflow-x-auto gap-2">
                                            <div className="flex w-full flex-col gap-1 first:pt-[3px]">
                                                <div className="w-full break-words">
                                                    <div className="chat-stream stream-table text-base leading-[1.75]">
                                                        {parse(DOMPurify.sanitize(content), options)}
                                                    </div>
                                                    {!!parsedAnswer.citations.length && <div className="font-semibold mb-1">Sources</div>}
                                                    {parsedAnswer.citations.map((x, i) => {
                                                        let title = x.replace(/\.\w+#page=/, " - Page ").replace(/\.mp4\?url=.*?t=([\d.]+)/, " - $1 seconds");
                                                        let citationTitle = x.replace(/\.\w+#page=.*$/, "").replace(/\.mp4\?url=.*$/, "");
                                                        const path = getCitationFilePath(x, appStateContext?.state.token?.accessToken);
                                                        return (
                                                            <a
                                                                key={i}
                                                                className="flex flex-col text-sstl-blue hover:underline cursor-pointer"
                                                                onClick={() => onCitationClicked(path, citationTitle)}
                                                            >
                                                                {`${++i}. ${title}`}
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {((messageId && !isStreaming) || !lastAnswer) && (
                                    <div className="mt-2 flex flex-col gap-3 ml-[-3px]">
                                        <div className="items-center justify-start rounded-xl p-1 flex">
                                            <div className="flex items-center">
                                                <span className="h-[30px] w-[30px]">
                                                    <ButtonTooltip title="Copy">
                                                        <button className="flex justify-center font-semibold text-sstl-blue" onClick={() => onCopyClicked()}>
                                                            <span className="material-symbols-outlined text-[1.25rem]">{copyIcon}</span>
                                                        </button>
                                                    </ButtonTooltip>
                                                </span>
                                                {!(sanitizedThoughts === "None") && (
                                                    <span className="h-[30px] w-[30px]">
                                                        <ButtonTooltip title={showThoughts ? "Hide thought process" : "Show thought process"}>
                                                            <button
                                                                className={
                                                                    "flex justify-center font-semibold text-sstl-blue pb-[3px]" +
                                                                    (showThoughts ? " icon-underline" : "")
                                                                }
                                                                onClick={() => onThoughtsClicked()}
                                                                disabled={!thoughts}
                                                            >
                                                                <span className="material-symbols-outlined text-[1.25rem]">lightbulb</span>
                                                            </button>
                                                        </ButtonTooltip>
                                                    </span>
                                                )}
                                                <span className="h-[30px] w-[30px]">
                                                    <ButtonTooltip title="Helpful">
                                                        <button
                                                            className={
                                                                "flex justify-center font-semibold text-sstl-blue pb-[3px]" +
                                                                (feedback === "Helpful" ? " icon-underline" : "")
                                                            }
                                                            onClick={onFeedbackHelpful}
                                                        >
                                                            <span className="material-symbols-outlined text-[1.25rem]">thumb_up</span>
                                                        </button>
                                                    </ButtonTooltip>
                                                </span>
                                                <span className="h-[30px] w-[30px]">
                                                    <ButtonTooltip title="Unhelpful">
                                                        <button
                                                            className={
                                                                "flex justify-center font-semibold text-sstl-blue pb-[3px]" +
                                                                (feedback === "Unhelpful" ? " icon-underline" : "")
                                                            }
                                                            onClick={onFeedbackUnhelpful}
                                                        >
                                                            <span className="material-symbols-outlined text-[1.25rem]">thumb_down</span>
                                                        </button>
                                                    </ButtonTooltip>
                                                </span>
                                                {showThoughts && (
                                                    <div className="card-shadow py-4 px-5">
                                                        <div className="font-semibold text-xl mb-2">Thought process</div>
                                                        <div
                                                            className="stream-table"
                                                            dangerouslySetInnerHTML={{
                                                                __html: sanitizedThoughts
                                                            }}
                                                        ></div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {feedback === "Helpful" && !feedbackComment ? (
                                            <div className="w-full border-transparent">
                                                <div className="relative flex flex-col rounded-xl bg-white shadow p-4 text-sm">
                                                    We're glad that you found this answer helpful. How much time do you think this answer saved you?
                                                    <nav className="flex min-w-[240px] flex-col gap-1 p-2">
                                                        {/* Option: It didn't save me any time */}
                                                        <div
                                                            role="button"
                                                            className="flex w-full items-center rounded-lg p-0 transition-all hover:bg-slate-100 focus:bg-slate-100 active:bg-slate-100"
                                                            onClick={() => radioRef0.current?.click()}
                                                        >
                                                            <label className="flex w-full cursor-pointer items-center px-3 py-2">
                                                                <div className="inline-flex items-center">
                                                                    <label className="relative flex items-center cursor-pointer">
                                                                        <input
                                                                            ref={radioRef0}
                                                                            name="framework"
                                                                            type="radio"
                                                                            className="peer h-5 w-5 cursor-pointer appearance-none rounded-full border border-slate-300 checked:border-slate-400 transition-all"
                                                                            id={`0-mins-${messageId}`}
                                                                            onChange={() => handleRadioChange("No time")}
                                                                        />
                                                                        <span className="absolute w-3 h-3 rounded-full opacity-0 peer-checked:opacity-100 transition-opacity duration-200 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></span>
                                                                    </label>
                                                                    <label className="ml-2 text-slate-600 cursor-pointer text-sm">
                                                                        It didn't save me any time
                                                                    </label>
                                                                </div>
                                                            </label>
                                                        </div>

                                                        {/* Option: Less 5 minutes */}
                                                        <div
                                                            role="button"
                                                            className="flex w-full items-center rounded-lg p-0 transition-all hover:bg-slate-100 focus:bg-slate-100 active:bg-slate-100"
                                                            onClick={() => radioRef5.current?.click()}
                                                        >
                                                            <label className="flex w-full cursor-pointer items-center px-3 py-2">
                                                                <div className="inline-flex items-center">
                                                                    <label className="relative flex items-center cursor-pointer">
                                                                        <input
                                                                            ref={radioRef5}
                                                                            name="framework"
                                                                            type="radio"
                                                                            className="peer h-5 w-5 cursor-pointer appearance-none rounded-full border border-slate-300 checked:border-slate-400 transition-all"
                                                                            id={`5-mins-${messageId}`}
                                                                            onChange={() => handleRadioChange("5 minutes")}
                                                                        />
                                                                        <span className="absolute w-3 h-3 rounded-full opacity-0 peer-checked:opacity-100 transition-opacity duration-200 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></span>
                                                                    </label>
                                                                    <label className="ml-2 text-slate-600 cursor-pointer text-sm">Less than 5 minutes</label>
                                                                </div>
                                                            </label>
                                                        </div>

                                                        {/* Option: Between 5-30 minutes */}
                                                        <div
                                                            role="button"
                                                            className="flex w-full items-center rounded-lg p-0 transition-all hover:bg-slate-100 focus:bg-slate-100 active:bg-slate-100"
                                                            onClick={() => radioRef5_30.current?.click()}
                                                        >
                                                            <label className="flex w-full cursor-pointer items-center px-3 py-2">
                                                                <div className="inline-flex items-center">
                                                                    <label className="relative flex items-center cursor-pointer">
                                                                        <input
                                                                            ref={radioRef5_30}
                                                                            name="framework"
                                                                            type="radio"
                                                                            className="peer h-5 w-5 cursor-pointer appearance-none rounded-full border border-slate-300 checked:border-slate-400 transition-all"
                                                                            id={`5-30-mins-${messageId}`}
                                                                            onChange={() => handleRadioChange("5-30 minutes")}
                                                                        />
                                                                        <span className="absolute w-3 h-3 rounded-full opacity-0 peer-checked:opacity-100 transition-opacity duration-200 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></span>
                                                                    </label>
                                                                    <label className="ml-2 text-slate-600 cursor-pointer text-sm">Between 5-30 minutes</label>
                                                                </div>
                                                            </label>
                                                        </div>

                                                        {/* Option: Over 30 minutes */}
                                                        <div
                                                            role="button"
                                                            className="flex w-full items-center rounded-lg p-0 transition-all hover:bg-slate-100 focus:bg-slate-100 active:bg-slate-100"
                                                            onClick={() => radioRef30.current?.click()}
                                                        >
                                                            <label className="flex w-full cursor-pointer items-center px-3 py-2">
                                                                <div className="inline-flex items-center">
                                                                    <label className="relative flex items-center cursor-pointer">
                                                                        <input
                                                                            ref={radioRef30}
                                                                            name="framework"
                                                                            type="radio"
                                                                            className="peer h-5 w-5 cursor-pointer appearance-none rounded-full border border-slate-300 checked:border-slate-400 transition-all"
                                                                            id={`30-mins-plus-${messageId}`}
                                                                            onChange={() => handleRadioChange("Over 30 minutes")}
                                                                        />
                                                                        <span className="absolute w-3 h-3 rounded-full opacity-0 peer-checked:opacity-100 transition-opacity duration-200 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></span>
                                                                    </label>
                                                                    <label className="ml-2 text-slate-600 cursor-pointer text-sm">Over 30 minutes</label>
                                                                </div>
                                                            </label>
                                                        </div>
                                                    </nav>
                                                    <div
                                                        className={
                                                            "absolute bottom-3 right-3 h-[28px] rounded-lg" +
                                                            (!feedbackOptionSelected ? " bg-sstl-light-grey" : " bg-sstl-yellow")
                                                        }
                                                    >
                                                        <a
                                                            className={
                                                                "group inline-flex items-center overflow-hidden transition-all duration-300" +
                                                                (!feedbackOptionSelected
                                                                    ? " cursor-default text-sstl-grey"
                                                                    : " text-sstl-white cursor-pointer hover:bg-opacity-80")
                                                            }
                                                            onClick={feedbackOptionSelected ? sendComment : undefined}
                                                        >
                                                            <span className="text-[1.75rem] material-symbols-outlined">arrow_upward_alt</span>
                                                            <span
                                                                className={
                                                                    "ml-0 mt-[2px] font-semibold transition-all duration-300 whitespace-nowrap overflow-hidden max-w-0" +
                                                                    (feedbackOptionSelected ? " group-hover:mr-2 group-hover:max-w-[100px]" : "")
                                                                }
                                                            >
                                                                Submit
                                                            </span>
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : feedback === "Unhelpful" && !feedbackComment ? (
                                            <div className="w-full border-transparent">
                                                <div className="relative flex flex-col rounded-xl bg-white shadow p-4 text-sm">
                                                    We're sorry that you found this answer unhelpful. Could you tell us why?
                                                    <nav className="flex min-w-[240px] flex-col gap-1 p-2">
                                                        {/* Option: Not relevant or what I asked for */}
                                                        <div
                                                            role="button"
                                                            className="flex w-full items-center rounded-lg p-0 transition-all hover:bg-slate-100 focus:bg-slate-100 active:bg-slate-100"
                                                            onClick={() => radioRefIrrelevant.current?.click()}
                                                        >
                                                            <label className="flex w-full cursor-pointer items-center px-3 py-2">
                                                                <div className="inline-flex items-center">
                                                                    <label className="relative flex items-center cursor-pointer">
                                                                        <input
                                                                            ref={radioRefIrrelevant}
                                                                            name="framework"
                                                                            type="radio"
                                                                            className="peer h-5 w-5 cursor-pointer appearance-none rounded-full border border-slate-300 checked:border-slate-400 transition-all"
                                                                            id={`irrelevant-${messageId}`}
                                                                            onChange={() => handleRadioChange("Not relevant or what I asked for")}
                                                                        />
                                                                        <span className="absolute w-3 h-3 rounded-full opacity-0 peer-checked:opacity-100 transition-opacity duration-200 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></span>
                                                                    </label>
                                                                    <label className="ml-2 text-slate-600 cursor-pointer text-sm">
                                                                        Not relevant or what I asked for
                                                                    </label>
                                                                </div>
                                                            </label>
                                                        </div>

                                                        {/* Option: Contained errors or was otherwise inaccurate */}
                                                        <div
                                                            role="button"
                                                            className="flex w-full items-center rounded-lg p-0 transition-all hover:bg-slate-100 focus:bg-slate-100 active:bg-slate-100"
                                                            onClick={() => radioRefErrors.current?.click()}
                                                        >
                                                            <label className="flex w-full cursor-pointer items-center px-3 py-2">
                                                                <div className="inline-flex items-center">
                                                                    <label className="relative flex items-center cursor-pointer">
                                                                        <input
                                                                            ref={radioRefErrors}
                                                                            name="framework"
                                                                            type="radio"
                                                                            className="peer h-5 w-5 cursor-pointer appearance-none rounded-full border border-slate-300 checked:border-slate-400 transition-all"
                                                                            id={`errors-${messageId}`}
                                                                            onChange={() => handleRadioChange("Contained errors or was otherwise inaccurate")}
                                                                        />
                                                                        <span className="absolute w-3 h-3 rounded-full opacity-0 peer-checked:opacity-100 transition-opacity duration-200 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></span>
                                                                    </label>
                                                                    <label className="ml-2 text-slate-600 cursor-pointer text-sm">
                                                                        Contained errors or was otherwise inaccurate
                                                                    </label>
                                                                </div>
                                                            </label>
                                                        </div>
                                                        <div
                                                            className={
                                                                "absolute bottom-3 right-3 h-[28px] rounded-lg" +
                                                                (!feedbackOptionSelected ? " bg-sstl-light-grey" : " bg-sstl-yellow")
                                                            }
                                                        >
                                                            <a
                                                                className={
                                                                    "group inline-flex items-center overflow-hidden transition-all duration-300" +
                                                                    (!feedbackOptionSelected
                                                                        ? " cursor-default text-sstl-grey"
                                                                        : " text-sstl-white cursor-pointer hover:bg-opacity-80")
                                                                }
                                                                onClick={feedbackOptionSelected ? sendComment : undefined}
                                                            >
                                                                <span className="text-[1.75rem] material-symbols-outlined">arrow_upward_alt</span>
                                                                <span
                                                                    className={
                                                                        "ml-0 mt-[2px] font-semibold transition-all duration-300 whitespace-nowrap overflow-hidden max-w-0" +
                                                                        (feedbackOptionSelected ? " group-hover:mr-2 group-hover:max-w-[100px]" : "")
                                                                    }
                                                                >
                                                                    Submit
                                                                </span>
                                                            </a>
                                                        </div>
                                                    </nav>
                                                </div>
                                            </div>
                                        ) : (
                                            feedbackComment && (
                                                <div className="relative flex flex-col rounded-xl bg-white shadow p-4 text-sm">Thanks for your feedback!</div>
                                            )
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
